import { sellableVariants } from '../../components/VariantDialog.tsx';
import { CustomFieldInputs, useCustomFields } from '../../components/CustomFields.tsx';
import { ratePct, standardRateNow, useTaxRates } from '../../lib/taxes.ts';
import { useMemo, useRef, useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t, tn } from '../../i18n/index.ts';
import { action, records } from '../../lib/api.ts';
import { money, parseMoney } from '../../lib/format.ts';
import { shrinkPhoto } from '../../lib/images.ts';
import { useImageUrl } from '../../lib/menu.ts';
import { usePlaces } from '../../lib/places.tsx';
import type { ComboGroup, ComboItem, LocationPrice, Place } from '../../lib/types.ts';
import { useUi } from '../../lib/ui-state.tsx';
import { ManageLayout, TONES, useAction, useRecords, useSave } from './common.tsx';
import { useLinkParam } from '../../lib/link-params.ts';
import { useSession } from '../../lib/session.tsx';
import {
  attributesError,
  draftAttributes,
  editOf,
  savedAttributes,
  variantEditError,
  VariantList,
  VariantsEditor,
  type DraftAttribute,
  type SavedAttribute,
  type VariantEdit,
} from './Variants.tsx';

interface Category {
  id: string;
  name: string;
  tone: (typeof TONES)[number];
  sequence: number;
  active: boolean;
  station: string | null;
}
interface Product {
  id: string;
  name: string;
  category: string | null;
  price: number;
  tax_rate: number;
  /** Where sales tax is added (United States): whether it is charged on this item. */
  taxable?: boolean;
  sku: string | null;
  barcode: string | null;
  plu?: string | null;
  description: string | null;
  sequence: number;
  active: boolean;
  available: boolean;
  station: string | null;
  kind: 'item' | 'combo';
  image: string | null;
  /** On a variant: its main product. */
  variant_of?: string | null;
  variant_values?: Record<string, string>;
  /** On a main product with variants: its attributes. */
  variant_attributes?: SavedAttribute[];
}
const hasVariants = (p: Product) => !!p.variant_attributes?.length;
interface FullCatalog {
  categories: Category[];
  products: Product[];
  modifier_groups: { id: string; name: string; min_select: number; max_select: number }[];
  product_modifier_groups: { id: string; product: string; group: string; sequence: number }[];
  combo_groups: ComboGroup[];
  combo_items: ComboItem[];
  location_prices?: LocationPrice[];
  sales_tax_anywhere?: boolean;
}
interface Station {
  id: string;
  name: string;
}

/** Lek have no cents in daily use, so prices are typed and shown in whole units where possible. */
const priceText = (minor: number) => (minor % 100 === 0 ? String(minor / 100) : (minor / 100).toFixed(2));

export function Menu() {
  const { fail, toast } = useUi();
  const { data, reload } = useAction<FullCatalog>('products.catalog', { include_inactive: true });
  const places = usePlaces();
  const { rules } = useTaxRates();
  const salesTax = rules?.tax === 'sales_tax';
  const { rows: stations } = useRecords<Station>('restaurant.station', { order_by: 'sequence' });
  const [category, setCategory] = useState<string | 'all'>('all');
  const [search, setSearch] = useState('');
  const [archived, setArchived] = useState(false);
  const [editing, setEditing] = useState<Product | 'new' | 'combo' | null>(null);
  // The search finds a product (?q=<name>); quick create starts one (?new=product).
  useLinkParam('q', setSearch);
  useLinkParam('new', (kind) => kind === 'product' && setEditing('new'));
  const [editingCat, setEditingCat] = useState<Category | 'new' | null>(null);

  const products = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Same order as the order screen: category by category, then each product's own order.
    const catOrder = new Map((data?.categories ?? []).map((c) => [c.id, c.sequence]));
    const rank = (p: Product) => (p.category ? (catOrder.get(p.category) ?? 0) : Number.MAX_SAFE_INTEGER);
    return (data?.products ?? [])
      .filter((p) => archived || p.active)
      // Variants show inside their product; a search finds them too.
      .filter((p) => !p.variant_of || !!q)
      .filter((p) =>
        q
          ? [p.name, p.sku ?? '', p.barcode ?? ''].some((x) => x.toLowerCase().includes(q))
          : category === 'all' || p.category === category,
      )
      .sort((a, b) => rank(a) - rank(b) || a.sequence - b.sequence || a.name.localeCompare(b.name));
  }, [data, search, category, archived]);

  const catName = (id: string | null) => data?.categories.find((c) => c.id === id)?.name ?? t('menu.no_category');
  const optionCount = (id: string) => data?.product_modifier_groups.filter((l) => l.product === id).length ?? 0;
  const comboCount = (id: string) => data?.combo_groups.filter((g) => g.combo === id).length ?? 0;
  const imageUrl = useImageUrl();

  const setAvailable = async (p: Product, available: boolean) => {
    try {
      await action('products.set_available', { product: p.id, available });
      toast(available ? t('menu.back_on_sale', { name: p.name }) : t('menu.sold_out_toast', { name: p.name }));
      void reload();
    } catch (err) {
      fail(err);
    }
  };

  return (
    <ManageLayout
      title={t('manage.menu')}
      subtitle={data ? t('menu.summary', { products: data.products.filter((p) => p.active).length, categories: data.categories.filter((c) => c.active).length }) : undefined}
      actions={
        <>
          <Button variant="secondary" icon="layers" onClick={() => setEditing('combo')} disabled={!data}>
            {t('menu.add_combo')}
          </Button>
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')} disabled={!data}>
            {t('menu.add_product')}
          </Button>
        </>
      }
    >
      <div className="menu-admin">
        <aside className="cat-list" aria-label={t('menu.categories')}>
          <div className="cat-list-head">
            <h2>{t('menu.categories')}</h2>
            <IconButton icon="plus" label={t('menu.add_category')} onClick={() => setEditingCat('new')} />
          </div>
          <button className={`cat-item${category === 'all' ? ' active' : ''}`} onClick={() => setCategory('all')}>
            <span className="cat-swatch tone-grey" aria-hidden="true" />
            <span className="cat-name">{t('menu.all_products')}</span>
            <span className="count">{data?.products.filter((p) => !p.variant_of && (archived || p.active)).length ?? ''}</span>
          </button>
          {!data
            ? Array.from({ length: 5 }, (_, i) => <Skeleton key={i} h={44} />)
            : data.categories
                .filter((c) => archived || c.active)
                .map((c) => (
                  <div key={c.id} className={`cat-item${category === c.id ? ' active' : ''}${c.active ? '' : ' archived'}`}>
                    <button className="cat-pick" onClick={() => setCategory(c.id)}>
                      <span className={`cat-swatch tone-${c.tone}`} aria-hidden="true" />
                      <span className="cat-name">{c.name}</span>
                      <span className="count">{data.products.filter((p) => p.category === c.id && !p.variant_of && (archived || p.active)).length}</span>
                    </button>
                    <IconButton icon="edit" label={t('menu.edit_category', { name: c.name })} onClick={() => setEditingCat(c)} />
                  </div>
                ))}
        </aside>

        <section className="product-admin">
          <div className="toolbar">
            <div className="search grow">
              <Icon name="search" size={18} />
              <input className="input" placeholder={t('menu.search')} aria-label={t('menu.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
              {search ? <IconButton icon="x" label={t('common.clear')} onClick={() => setSearch('')} /> : null}
            </div>
            <button className={`chip${archived ? ' active' : ''}`} aria-pressed={archived} onClick={() => setArchived((v) => !v)}>
              <Icon name="archive" size={16} /> {t('menu.show_archived')}
            </button>
          </div>

          {!data ? (
            <Skeleton h={320} r="var(--radius-lg)" />
          ) : products.length === 0 ? (
            <EmptyState
              icon="receipt"
              title={search ? t('order.no_results') : t('menu.empty')}
              body={search ? undefined : t('menu.empty_body')}
              action={
                search ? undefined : (
                  <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
                    {t('menu.add_product')}
                  </Button>
                )
              }
            />
          ) : (
            <div className="list-card" role="list">
              {products.map((p) => (
                <div key={p.id} role="listitem" className={`list-row${p.active ? '' : ' archived'}`}>
                  <button className="list-main with-thumb" onClick={() => setEditing(p)}>
                    <span className="menu-thumb" aria-hidden="true">
                      {imageUrl(p) ? <img src={imageUrl(p)!} alt="" loading="lazy" /> : <Icon name={p.kind === 'combo' ? 'layers' : 'receipt'} size={18} />}
                    </span>
                    <span className="list-text">
                      <span className="list-title">{p.name}</span>
                      <span className="list-sub">
                        {category === 'all' || search ? `${catName(p.category)} · ` : ''}
                        {p.kind === 'combo'
                          ? tn('menu.combo_groups', comboCount(p.id))
                          : salesTax
                            ? t(p.taxable === false ? 'menu.not_taxed' : 'menu.taxed')
                            : `${t('menu.vat', { n: p.tax_rate })}${data?.sales_tax_anywhere && p.taxable === false ? ` · ${t('menu.not_taxed')}` : ''}`}
                        {optionCount(p.id) && p.kind !== 'combo' ? ` · ${tn('menu.option_groups', optionCount(p.id))}` : ''}
                        {hasVariants(p) ? ` · ${tn('variants.count', data?.products.filter((v) => v.variant_of === p.id && v.active).length ?? 0)}` : ''}
                      </span>
                    </span>
                  </button>
                  <span className="list-price">{money(p.price)}</span>
                  {!p.active ? (
                    <StatusBadge tone="neutral" icon="archive" label={t('menu.archived')} />
                  ) : p.available ? (
                    <StatusBadge tone="ok" icon="check" label={t('menu.on_sale')} />
                  ) : (
                    <StatusBadge tone="danger" icon="x" label={t('product.sold_out')} />
                  )}
                  <Switch
                    checked={p.available}
                    disabled={!p.active}
                    label={p.available ? t('menu.mark_sold_out', { name: p.name }) : t('menu.mark_on_sale', { name: p.name })}
                    onChange={(v) => void setAvailable(p, v)}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {editing && data ? (
        <ProductDialog
          product={editing === 'new' || editing === 'combo' ? null : editing}
          newKind={editing === 'combo' ? 'combo' : 'item'}
          catalog={data}
          stations={stations ?? []}
          places={places.multi ? places.places : []}
          defaultCategory={category === 'all' ? (data.categories.find((c) => c.active)?.id ?? null) : category}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      ) : null}
      {editingCat && data ? (
        <CategoryDialog
          category={editingCat === 'new' ? null : editingCat}
          stations={stations ?? []}
          nextSequence={Math.max(0, ...data.categories.map((c) => c.sequence)) + 10}
          onClose={() => setEditingCat(null)}
          onSaved={(c) => {
            setEditingCat(null);
            setCategory(c.id);
            void reload();
          }}
        />
      ) : null}
    </ManageLayout>
  );
}

interface DraftGroup {
  key: string;
  name: string;
  choose: number;
  items: { product: string; price: string }[];
}

interface DraftPlace {
  location: string;
  /** Empty: the menu price. */
  price: string;
  on_menu: boolean;
}

function ProductDialog({
  product,
  newKind,
  catalog,
  stations,
  places,
  defaultCategory,
  onClose,
  onSaved,
}: {
  product: Product | null;
  newKind: 'item' | 'combo';
  catalog: FullCatalog;
  stations: Station[];
  /** Open locations, when there is more than one: each can sell at its own price or leave it off. */
  places: Place[];
  defaultCategory: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { busy, run } = useSave();
  const linked = catalog.product_modifier_groups.filter((l) => product && l.product === product.id);
  const [name, setName] = useState(product?.name ?? '');
  const [categoryId, setCategoryId] = useState(product ? (product.category ?? '') : (defaultCategory ?? ''));
  const [price, setPrice] = useState(product ? priceText(product.price) : '');
  const rates = useTaxRates();
  const salesTax = rates.rules?.tax === 'sales_tax';
  // A business with VAT at home and a location that adds sales tax asks both.
  const askTaxable = salesTax || !!catalog.sales_tax_anywhere;
  const [vat, setVat] = useState(product?.tax_rate ?? (salesTax ? 0 : standardRateNow()));
  const [taxable, setTaxable] = useState(product?.taxable ?? true);
  const [station, setStation] = useState(product?.station ?? '');
  const [groups, setGroups] = useState<string[]>(linked.map((l) => l.group));
  const [sku, setSku] = useState(product?.sku ?? '');
  const [barcode, setBarcode] = useState(product?.barcode ?? '');
  const [plu, setPlu] = useState(product?.plu ?? '');
  const [description, setDescription] = useState(product?.description ?? '');
  const [tried, setTried] = useState(false);
  const cf = useCustomFields('product.product', product?.id);
  const { can } = useSession();
  // Variants: only for a single item that is not a variant itself.
  const canVary = (product?.kind ?? newKind) === 'item' && !product?.variant_of;
  const master = product?.variant_of ? catalog.products.find((p) => p.id === product.variant_of) : undefined;
  const [attrs, setAttrs] = useState<DraftAttribute[]>(() => draftAttributes(product?.variant_attributes));
  const variants = product ? sellableVariants(catalog, product.id, true) : [];
  const [variantEdits, setVariantEdits] = useState<Record<string, VariantEdit>>({});
  const [ownStock, setOwnStock] = useState(true);
  const [local, setLocal] = useState<DraftPlace[]>(() =>
    places.map((pl) => {
      const row = catalog.location_prices?.find((r) => product && r.product === product.id && r.location === pl.id);
      return { location: pl.id, price: row?.price != null ? priceText(row.price) : '', on_menu: row?.on_menu ?? true };
    }),
  );
  const kind = product?.kind ?? newKind;
  const { fail } = useUi();
  const imageUrl = useImageUrl();
  // undefined: unchanged; null: removed; otherwise the new photo, already made small.
  const [photo, setPhoto] = useState<{ base64: string; url: string } | null | undefined>(undefined);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const shownPhoto = photo === undefined ? (product ? imageUrl(product) : null) : (photo?.url ?? null);
  const [combo, setCombo] = useState<DraftGroup[]>(() =>
    product
      ? catalog.combo_groups
          .filter((g) => g.combo === product.id)
          .sort((a, b) => a.sequence - b.sequence)
          .map((g) => ({
            key: g.id,
            name: g.name,
            choose: g.choose,
            items: catalog.combo_items
              .filter((i) => i.group === g.id)
              .sort((a, b) => a.sequence - b.sequence)
              .map((i) => ({ product: i.product, price: i.price_delta ? priceText(i.price_delta) : '' })),
          }))
      : kind === 'combo'
        ? [
            { key: 'g1', name: t('combo.default_main'), choose: 1, items: [] },
            { key: 'g2', name: t('combo.default_drink'), choose: 1, items: [] },
          ]
        : [],
  );

  const pickPhoto = async (file: File | undefined) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      setPhoto(await shrinkPhoto(file));
    } catch {
      fail(new Error(t('menu.photo_invalid')));
    } finally {
      setPhotoBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const minor = parseMoney(price);
  const comboError =
    kind !== 'combo'
      ? null
      : combo.length === 0
        ? t('combo.need_group')
        : combo.find((g) => !g.name.trim())
          ? t('combo.need_name')
          : combo.find((g) => g.items.length === 0)
            ? t('combo.need_items', { group: combo.find((g) => g.items.length === 0)!.name || '…' })
            : combo.find((g) => g.items.some((i) => i.price.trim() && parseMoney(i.price) === null))
              ? t('menu.price_invalid')
              : null;
  const errors = {
    name: name.trim() ? null : t('form.required'),
    price: minor === null ? t('menu.price_invalid') : null,
    combo: comboError,
    local: local.some((l) => l.price.trim() && parseMoney(l.price) === null) ? t('menu.price_invalid') : null,
    plu: plu.trim() && !/^\d{1,6}$/.test(plu.trim()) ? t('menu.plu_rule') : null,
    variants: canVary ? (attributesError(attrs) ?? variantEditError(variantEdits)) : null,
  };
  const catStation = stations.find((s) => s.id === catalog.categories.find((c) => c.id === categoryId)?.station)?.name;

  const save = (active = product?.active ?? true) => {
    setTried(true);
    if (errors.name || errors.price || errors.combo || errors.local || errors.plu || errors.variants || !cf.valid) return;
    void run(async () => {
      const values = {
        name: name.trim(),
        category: categoryId || null,
        price: minor,
        // Where sales tax is added the location sets the rate; the item only says if it is taxed.
        tax_rate: salesTax ? (product?.tax_rate ?? 0) : vat,
        taxable,
        station: kind === 'combo' ? null : station || null,
        kind,
        sku: sku.trim() || null,
        barcode: barcode.trim() || null,
        plu: plu.trim() || null,
        description: description.trim() || null,
        active,
      };
      const saved = product
        ? await records.update<Product>('product.product', product.id, values)
        : await records.create<Product>('product.product', {
            ...values,
            sequence: Math.max(0, ...catalog.products.filter((p) => p.category === categoryId).map((p) => p.sequence)) + 10,
          });
      await cf.save(saved.id);
      if (photo !== undefined) await action('products.set_image', { product: saved.id, data: photo?.base64 ?? null });
      if (kind === 'combo') {
        await action('products.save_combo', {
          product: saved.id,
          groups: combo.map((g) => ({
            name: g.name.trim(),
            choose: Math.min(g.choose, g.items.length),
            items: g.items.map((i) => ({ product: i.product, price_delta: i.price.trim() ? parseMoney(i.price)! : 0 })),
          })),
        });
      }
      if (local.length) {
        await action('products.set_location_prices', {
          product: saved.id,
          locations: local.map((l) => ({ location: l.location, price: l.price.trim() ? parseMoney(l.price)! : null, on_menu: l.on_menu })),
        });
      }
      if (canVary && (attrs.length || (product && hasVariants(product)))) {
        // Also when only the name or category changed: the variants follow their product.
        await action('products.save_variants', { product: saved.id, attributes: savedAttributes(attrs) });
        for (const v of variants) {
          const e = variantEdits[v.id];
          if (!e) continue;
          const was = editOf(v);
          if (e.price === was.price && e.sku === was.sku && e.barcode === was.barcode && e.active === was.active) continue;
          if (!attrs.length) continue; // all archived anyway
          await records.update('product.product', v.id, { price: parseMoney(e.price)!, sku: e.sku.trim() || null, barcode: e.barcode.trim() || null, active: e.active });
        }
        if (attrs.length && ownStock && can('inventory.manage')) await action('inventory.variant_items', { product: saved.id });
      }
      for (const l of linked) if (!groups.includes(l.group)) await records.remove('product.product_modifier_group', l.id);
      for (const [i, g] of groups.entries()) {
        if (!linked.some((l) => l.group === g)) {
          await records.create('product.product_modifier_group', { product: saved.id, group: g, sequence: (i + 1) * 10 });
        }
      }
    }, product ? (active === product.active ? t('menu.saved', { name: name.trim() }) : active ? t('menu.restored', { name: name.trim() }) : t('menu.archived_toast', { name: name.trim() })) : t('menu.added', { name: name.trim() })).then((ok) => ok && onSaved());
  };

  return (
    <Dialog
      title={product ? product.name : kind === 'combo' ? t('menu.add_combo') : t('menu.add_product')}
      onClose={onClose}
      footer={
        <>
          {product ? (
            <Button variant="ghost" icon={product.active ? 'archive' : 'undo'} onClick={() => save(!product.active)} disabled={busy}>
              {product.active ? t('menu.archive') : t('menu.restore')}
            </Button>
          ) : (
            <span />
          )}
          <Button variant="primary" icon="check" onClick={() => save()} busy={busy} disabled={photoBusy}>
            {product ? t('common.save') : kind === 'combo' ? t('menu.add_combo') : t('menu.add_product')}
          </Button>
        </>
      }
    >
      <div className="photo-field">
        <div className="photo-box" aria-hidden={!shownPhoto}>
          {shownPhoto ? <img src={shownPhoto} alt={t('menu.photo_of', { name: name || '…' })} /> : <Icon name="image" size={28} />}
        </div>
        <div className="photo-actions">
          <span className="photo-label">{t('menu.photo')}</span>
          <span className="muted">{t('menu.photo_hint')}</span>
          <div className="row-actions">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => void pickPhoto(e.target.files?.[0])} />
            <Button variant="secondary" size="sm" icon="image" onClick={() => fileRef.current?.click()} busy={photoBusy}>
              {shownPhoto ? t('menu.photo_change') : t('menu.photo_add')}
            </Button>
            {shownPhoto ? (
              <Button variant="ghost" size="sm" icon="trash" onClick={() => setPhoto(null)}>
                {t('menu.photo_remove')}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="form-grid">
        <Field label={t('menu.name')} error={tried ? errors.name : null} hint={master ? t('variants.name_follows', { name: master.name }) : undefined}>
          <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} readOnly={!!master} data-autofocus />
        </Field>
        <Field
          label={kind === 'combo' ? t('combo.price') : t('menu.price')}
          hint={kind === 'combo' ? t('combo.price_hint') : attrs.length ? t('variants.price_hint') : t('menu.price_hint')} error={tried ? errors.price : null}>
          <input className="input" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0" />
        </Field>
        <Field label={t('menu.category')}>
          <select className="input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">{t('menu.no_category')}</option>
            {catalog.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.active ? '' : ` (${t('menu.archived')})`}
              </option>
            ))}
          </select>
        </Field>
        {kind === 'combo' ? null : (
          <Field label={t('menu.station')} hint={t('menu.station_hint')}>
            <select className="input" value={station} onChange={(e) => setStation(e.target.value)}>
              <option value="">{catStation ? t('menu.station_from_category', { name: catStation }) : t('menu.station_none')}</option>
              {stations.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>

      {kind === 'combo' ? (
        <ComboEditor groups={combo} onChange={setCombo} catalog={catalog} error={tried ? errors.combo : null} />
      ) : null}

      {places.length ? (
        <fieldset className="option-group">
          <legend>
            {t('menu.by_location')} <span className="muted">{t('menu.by_location_hint')}</span>
          </legend>
          <div className="place-prices">
            {places.map((pl) => {
              const l = local.find((x) => x.location === pl.id)!;
              const set = (patch: Partial<DraftPlace>) => setLocal((xs) => xs.map((x) => (x.location === pl.id ? { ...x, ...patch } : x)));
              const bad = !!l.price.trim() && parseMoney(l.price) === null;
              return (
                <div key={pl.id} className={`place-price${l.on_menu ? '' : ' off'}${bad && tried ? ' field-error' : ''}`}>
                  <span className="place-badge" aria-hidden="true">
                    {pl.code}
                  </span>
                  <span className="place-price-name">{pl.name}</span>
                  <input
                    className="input place-price-input"
                    inputMode="decimal"
                    value={l.price}
                    disabled={!l.on_menu}
                    placeholder={minor !== null ? priceText(minor) : '0'}
                    aria-label={t('menu.price_at', { name: pl.name })}
                    aria-invalid={bad && tried}
                    onChange={(e) => set({ price: e.target.value })}
                  />
                  <Switch checked={l.on_menu} onChange={(on) => set({ on_menu: on })} label={t('menu.sold_at', { name: pl.name })} />
                </div>
              );
            })}
          </div>
          {tried && errors.local ? <span className="field-msg" role="alert">
              <Icon name="alert" size={14} /> {errors.local}
            </span> : null}
        </fieldset>
      ) : null}

      {askTaxable ? (
        <fieldset className="option-group" hidden={kind === 'combo'}>
          <legend>
            {t('menu.sales_tax')} <span className="muted">{t(salesTax ? 'menu.sales_tax_hint' : 'menu.sales_tax_hint_some')}</span>
          </legend>
          <div className="segmented" role="radiogroup" aria-label={t('menu.sales_tax')}>
            {[true, false].map((on) => (
              <button key={String(on)} role="radio" aria-checked={taxable === on} className={taxable === on ? 'active' : ''} onClick={() => setTaxable(on)}>
                {t(on ? 'menu.taxed' : 'menu.not_taxed')}
              </button>
            ))}
          </div>
        </fieldset>
      ) : null}
      {salesTax ? null : (
        <fieldset className="option-group" hidden={kind === 'combo'}>
          <legend>{t('menu.vat_rate')}</legend>
          <div className="segmented" role="radiogroup" aria-label={t('menu.vat_rate')}>
            {rates.choices(vat).map((r) => (
              <button key={r} role="radio" aria-checked={vat === r} className={vat === r ? 'active' : ''} onClick={() => setVat(r)}>
                {ratePct(r)}
              </button>
            ))}
          </div>
        </fieldset>
      )}

      <fieldset className="option-group" hidden={kind === 'combo'}>
        <legend>
          {t('menu.options')} <span className="muted">{t('menu.options_hint')}</span>
        </legend>
        {catalog.modifier_groups.length === 0 ? (
          <p className="muted">{t('menu.no_option_groups')}</p>
        ) : (
          <div className="option-list">
            {catalog.modifier_groups.map((g) => {
              const on = groups.includes(g.id);
              return (
                <button
                  key={g.id}
                  role="checkbox"
                  aria-checked={on}
                  className={`option${on ? ' active' : ''}`}
                  onClick={() => setGroups((x) => (on ? x.filter((y) => y !== g.id) : [...x, g.id]))}
                >
                  <span className="option-check">{on ? <Icon name="check" size={16} /> : null}</span>
                  <span className="option-name">{g.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </fieldset>

      {canVary ? (
        <>
          <VariantsEditor attributes={attrs} onChange={setAttrs} error={tried ? errors.variants : null} basePrice={minor} />
          {attrs.length ? (
            <VariantList variants={variants} edits={variantEdits} onEdit={(id, e) => setVariantEdits((s) => ({ ...s, [id]: e }))} />
          ) : null}
          {attrs.length && can('inventory.manage') ? (
            <label className="check-row">
              <input type="checkbox" checked={ownStock} onChange={(e) => setOwnStock(e.target.checked)} />
              <span>
                {t('variants.own_stock')} <span className="muted">{t('variants.own_stock_hint')}</span>
              </span>
            </label>
          ) : null}
        </>
      ) : null}

      <details className="more-fields" open={!!(product?.sku || product?.barcode || product?.plu || product?.description)}>
        <summary>{t('menu.more_details')}</summary>
        <div className="form-grid">
          <Field label={t('menu.sku')} hint={t('menu.sku_hint')}>
            <input className="input" value={sku} maxLength={40} onChange={(e) => setSku(e.target.value)} />
          </Field>
          <Field label={t('menu.barcode')}>
            <input className="input" value={barcode} maxLength={40} inputMode="numeric" onChange={(e) => setBarcode(e.target.value)} />
          </Field>
          <Field label={t('menu.plu')} hint={t('menu.plu_hint')} error={errors.plu}>
            <input className="input" value={plu} maxLength={6} inputMode="numeric" onChange={(e) => setPlu(e.target.value)} />
          </Field>
        </div>
        <Field label={t('menu.description')}>
          <textarea className="input textarea" rows={2} value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </details>
      <CustomFieldInputs cf={cf} tried={tried} />
    </Dialog>
  );
}

/** The choices of a combo menu: groups such as "Main" and "Drink", each with the items it offers. */
function ComboEditor({
  groups,
  onChange,
  catalog,
  error,
}: {
  groups: DraftGroup[];
  onChange: (g: DraftGroup[]) => void;
  catalog: FullCatalog;
  error: string | null;
}) {
  const items = catalog.products.filter((p) => p.kind !== 'combo' && p.active && !hasVariants(p)).sort((a, b) => a.name.localeCompare(b.name));
  const set = (key: string, change: Partial<DraftGroup>) => onChange(groups.map((g) => (g.key === key ? { ...g, ...change } : g)));
  const nameOf = (id: string) => catalog.products.find((p) => p.id === id);
  return (
    <fieldset className="option-group combo-editor">
      <legend>
        {t('combo.choices')} <span className="muted">{t('combo.choices_hint')}</span>
      </legend>
      {groups.map((g, gi) => (
        <div key={g.key} className="combo-group-card">
          <div className="combo-group-head">
            <Field label={t('combo.group_name')}>
              <input className="input" value={g.name} maxLength={60} placeholder={t('combo.default_main')} onChange={(e) => set(g.key, { name: e.target.value })} />
            </Field>
            <Field label={t('combo.guest_picks')}>
              <select className="input" value={g.choose} onChange={(e) => set(g.key, { choose: Number(e.target.value) })}>
                {Array.from({ length: Math.max(1, Math.min(10, g.items.length || 1)) }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n === g.items.length && g.items.length > 0 ? t('combo.all_included', { n }) : n}
                  </option>
                ))}
              </select>
            </Field>
            <div className="combo-group-tools">
              <IconButton icon="up" label={t('combo.move_up')} disabled={gi === 0} onClick={() => onChange(groups.map((x, i) => (i === gi - 1 ? g : i === gi ? groups[gi - 1]! : x)))} />
              <IconButton icon="trash" label={t('combo.remove_group', { name: g.name || '…' })} onClick={() => onChange(groups.filter((x) => x.key !== g.key))} />
            </div>
          </div>
          {g.items.length ? (
            <ul className="combo-items">
              {g.items.map((it) => {
                const p = nameOf(it.product);
                return (
                  <li key={it.product}>
                    <span className="combo-item-name">
                      {p?.name ?? '?'}
                      <span className="muted"> · {money(p?.price ?? 0)}</span>
                    </span>
                    <label className="combo-extra">
                      <span className="muted">{t('combo.extra')}</span>
                      <input
                        className="input"
                        inputMode="decimal"
                        placeholder="0"
                        value={it.price}
                        aria-label={t('combo.extra_for', { name: p?.name ?? '' })}
                        onChange={(e) => set(g.key, { items: g.items.map((x) => (x.product === it.product ? { ...x, price: e.target.value } : x)) })}
                      />
                    </label>
                    <IconButton
                      icon="x"
                      label={t('combo.remove_item', { name: p?.name ?? '' })}
                      onClick={() => set(g.key, { items: g.items.filter((x) => x.product !== it.product), choose: Math.max(1, Math.min(g.choose, g.items.length - 1)) })}
                    />
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="muted">{t('combo.no_items')}</p>
          )}
          <select
            className="input combo-add"
            value=""
            aria-label={t('combo.add_item_to', { name: g.name || '…' })}
            onChange={(e) => e.target.value && set(g.key, { items: [...g.items, { product: e.target.value, price: '' }] })}
          >
            <option value="">{t('combo.add_item')}</option>
            {catalog.categories.map((c) => {
              const inCat = items.filter((p) => p.category === c.id && !g.items.some((x) => x.product === p.id));
              return inCat.length ? (
                <optgroup key={c.id} label={c.name}>
                  {inCat.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {money(p.price)}
                    </option>
                  ))}
                </optgroup>
              ) : null;
            })}
          </select>
        </div>
      ))}
      <Button
        variant="secondary"
        icon="plus"
        onClick={() => onChange([...groups, { key: `n${Date.now()}`, name: '', choose: 1, items: [] }])}
        disabled={groups.length >= 10}
      >
        {t('combo.add_group')}
      </Button>
      {error ? (
        <p className="callout callout-warn" role="alert">
          <Icon name="alert" size={16} /> {error}
        </p>
      ) : null}
    </fieldset>
  );
}

function CategoryDialog({
  category,
  stations,
  nextSequence,
  onClose,
  onSaved,
}: {
  category: Category | null;
  stations: Station[];
  nextSequence: number;
  onClose: () => void;
  onSaved: (c: Category) => void;
}) {
  const { busy, run } = useSave();
  const [name, setName] = useState(category?.name ?? '');
  const [tone, setTone] = useState<Category['tone']>(category?.tone ?? 'blue');
  const [station, setStation] = useState(category?.station ?? '');
  const [tried, setTried] = useState(false);

  const save = (active = category?.active ?? true) => {
    setTried(true);
    if (!name.trim()) return;
    let saved: Category | null = null;
    void run(async () => {
      const values = { name: name.trim(), tone, station: station || null, active };
      saved = category
        ? await records.update<Category>('product.category', category.id, values)
        : await records.create<Category>('product.category', { ...values, sequence: nextSequence });
    }, category ? t('menu.saved', { name: name.trim() }) : t('menu.added', { name: name.trim() })).then((ok) => ok && saved && onSaved(saved));
  };

  return (
    <Dialog
      title={category ? category.name : t('menu.add_category')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          {category ? (
            <Button variant="ghost" icon={category.active ? 'archive' : 'undo'} onClick={() => save(!category.active)} disabled={busy}>
              {category.active ? t('menu.archive') : t('menu.restore')}
            </Button>
          ) : (
            <span />
          )}
          <Button variant="primary" icon="check" onClick={() => save()} busy={busy}>
            {category ? t('common.save') : t('menu.add_category')}
          </Button>
        </>
      }
    >
      <Field label={t('menu.name')} error={tried && !name.trim() ? t('form.required') : null}>
        <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} data-autofocus />
      </Field>
      <fieldset className="option-group">
        <legend>{t('menu.colour')}</legend>
        <div className="tone-picker" role="radiogroup" aria-label={t('menu.colour')}>
          {TONES.map((x) => (
            <button
              key={x}
              role="radio"
              aria-checked={tone === x}
              aria-label={t(`tone.${x}`)}
              title={t(`tone.${x}`)}
              className={`tone-dot tone-${x}${tone === x ? ' active' : ''}`}
              onClick={() => setTone(x)}
            >
              {tone === x ? <Icon name="check" size={16} /> : null}
            </button>
          ))}
        </div>
      </fieldset>
      <Field label={t('menu.station')} hint={t('menu.category_station_hint')}>
        <select className="input" value={station} onChange={(e) => setStation(e.target.value)}>
          <option value="">{t('menu.station_none')}</option>
          {stations.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </Field>
      {category && !category.active ? <p className="muted">{t('menu.category_archived_hint')}</p> : null}
    </Dialog>
  );
}
